import { useQuery } from '@apollo/client';
import type { Bundle, Patient } from '@medplum/fhirtypes';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';

import {
  Button,
  ColDefBuilder,
  DataTable,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Text,
  makeDefaultSortableColumnDef,
} from '@eluve/components';
import { formatDate } from '@eluve/date-utils';

import { useMedplum } from './MedplumProvider';
import { useTabActionsSlot } from './TabActionsSlotContext';
import { PATIENT_BY_IDENTIFIER_QUERY } from './fhirQueries';

interface CareTeamRow {
  id: string;
  name: string;
  /** Care-team role. Today every member comes from the patient's
   *  `generalPractitioner` list, so this is constant; a future FHIR `CareTeam`
   *  resource would vary it per participant (GP, nurse, specialist, …). */
  role: string;
  email: string | null;
  reference: string;
}

interface HistoryEntry {
  versionId: string;
  lastUpdated: string | null;
  source: string | null;
  generalPractitionerRefs: string[];
}

/**
 * "Care Team" sub-tab of the Medical Profile. Today the team is the patient's
 * FHIR `generalPractitioner` list (read-only) plus its assignment history; the
 * History action is portaled into the tab bar's action slot. A future iteration
 * can back this with a real FHIR `CareTeam` resource to add/remove members.
 */
export const CareTeam: React.FC = () => {
  const { patientId } = useParams() as { patientId: string };
  const { client, fhirClient } = useMedplum();
  const actionsSlot = useTabActionsSlot();

  const { data: patientData, loading: isPatientLoading } = useQuery(
    PATIENT_BY_IDENTIFIER_QUERY,
    {
      client: fhirClient ?? undefined,
      variables: { identifier: patientId },
      skip: !fhirClient,
      fetchPolicy: 'cache-and-network',
    },
  );

  const patient = patientData?.PatientList?.[0] ?? null;
  const fhirPatientId = patient?.id ?? null;

  const careTeam: CareTeamRow[] =
    patient?.generalPractitioner
      ?.map((gp) => {
        // `resource` is a union over every possible Reference target; only
        // Practitioner has the fields selected by the inline fragment.
        const resource = gp?.resource;
        const practitioner =
          resource?.__typename === 'Practitioner' ? resource : null;
        if (!practitioner?.id) return null;
        const nameEntry = practitioner.name?.[0];
        const given = nameEntry?.given?.join(' ') ?? '';
        const family = nameEntry?.family ?? '';
        const fullName = `${given} ${family}`.trim();
        const email =
          practitioner.telecom?.find((t) => t.system === 'email')?.value ??
          null;
        return {
          id: practitioner.id,
          name: fullName || 'Unnamed practitioner',
          role: 'General Practitioner',
          email,
          reference: `Practitioner/${practitioner.id}`,
        };
      })
      .filter((row): row is CareTeamRow => row !== null) ?? [];

  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [practitionerNames, setPractitionerNames] = useState<
    Record<string, string>
  >({});
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  useEffect(() => {
    if (!isHistoryOpen || !client || !fhirPatientId) return;

    let cancelled = false;
    setIsLoadingHistory(true);

    (async () => {
      try {
        const bundle = (await client.readHistory(
          'Patient',
          fhirPatientId,
        )) as Bundle<Patient>;

        const entries: HistoryEntry[] = (bundle.entry ?? [])
          .map((entry) => {
            const resource = entry.resource;
            if (!resource) return null;
            return {
              versionId: resource.meta?.versionId ?? '',
              lastUpdated: resource.meta?.lastUpdated ?? null,
              source: resource.meta?.source ?? null,
              generalPractitionerRefs:
                resource.generalPractitioner
                  ?.map((r) => r.reference)
                  .filter((ref): ref is string => Boolean(ref)) ?? [],
            };
          })
          .filter((entry): entry is HistoryEntry => entry !== null);

        if (cancelled) return;
        setHistory(entries);

        const distinctIds = Array.from(
          new Set(
            entries
              .flatMap((e) => e.generalPractitionerRefs)
              .map((ref) => ref.replace(/^Practitioner\//, '')),
          ),
        ).filter(Boolean);

        if (distinctIds.length > 0) {
          const practitioners = await client.searchResources('Practitioner', {
            _id: distinctIds.join(','),
            _count: distinctIds.length,
          });
          if (cancelled) return;
          const map: Record<string, string> = {};
          for (const p of practitioners) {
            if (!p.id) continue;
            const entry = p.name?.[0];
            const full =
              `${entry?.given?.join(' ') ?? ''} ${entry?.family ?? ''}`.trim();
            map[p.id] = full || 'Unnamed practitioner';
          }
          setPractitionerNames(map);
        }
      } catch {
        if (!cancelled) setHistory([]);
      } finally {
        if (!cancelled) setIsLoadingHistory(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isHistoryOpen, client, fhirPatientId]);

  const refToName = (ref: string): string => {
    const id = ref.replace(/^Practitioner\//, '');
    return practitionerNames[id] ?? id.slice(0, 8);
  };

  const columns = useMemo(
    () =>
      new ColDefBuilder<CareTeamRow>()
        .colDef({
          ...makeDefaultSortableColumnDef('name', 'Name'),
          id: 'name',
          // Stretch Name to absorb slack so the other columns sit flush right
          // (matches the Patient Uploads table).
          meta: { cellClassName: 'w-full', headerClassName: 'w-full' },
          cell: ({ row }) => (
            <Text weight="medium" className="whitespace-nowrap">
              {row.original.name}
            </Text>
          ),
        })
        .defaultSortable('role', 'Role')
        .colDef({
          id: 'email',
          header: 'Contact',
          accessorKey: 'email',
          cell: ({ row }) =>
            row.original.email ? (
              <span className="whitespace-nowrap">{row.original.email}</span>
            ) : (
              <span className="text-text-tertiary">—</span>
            ),
        })
        .colDef({
          id: 'reference',
          header: 'Reference',
          cell: ({ row }) => (
            <Text
              size="xs"
              color="tertiary"
              className="font-mono whitespace-nowrap"
            >
              {row.original.reference}
            </Text>
          ),
        })
        .build(),
    [],
  );

  if (isPatientLoading && !patientData) {
    return <Text color="tertiary">Loading…</Text>;
  }

  if (!fhirPatientId) {
    return (
      <Text size="sm" color="tertiary">
        No FHIR record is available for this patient.
      </Text>
    );
  }

  // The History action lives in the tab bar (this tab's action slot), so it's
  // grouped with the tabs rather than stacked above the list.
  const historyAction = (
    <Sheet open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm">
          History
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[420px] sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Assignment history</SheetTitle>
          <SheetDescription>
            Every change to this patient's general practitioners, pulled from
            the FHIR resource history.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 px-6 pb-6">
          {isLoadingHistory ? (
            <Text color="tertiary">Loading…</Text>
          ) : history.length === 0 ? (
            <Text color="tertiary">No history available.</Text>
          ) : (
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-border-primary text-left text-[10px] font-medium tracking-wide text-text-tertiary uppercase">
                  <th className="py-1.5 pr-3 font-medium">When</th>
                  <th className="py-1.5 pr-3 font-medium">Change</th>
                  <th className="py-1.5 pr-3 text-right font-medium">GPs</th>
                  <th className="py-1.5 text-right font-medium">Version</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, index) => {
                  const previous = history[index + 1];
                  const currentRefs = new Set(h.generalPractitionerRefs);
                  const previousRefs = new Set(
                    previous?.generalPractitionerRefs ?? [],
                  );
                  const added = previous
                    ? h.generalPractitionerRefs.filter(
                        (r) => !previousRefs.has(r),
                      )
                    : h.generalPractitionerRefs;
                  const removed = previous
                    ? previous.generalPractitionerRefs.filter(
                        (r) => !currentRefs.has(r),
                      )
                    : [];
                  const count = h.generalPractitionerRefs.length;
                  return (
                    <tr
                      key={h.versionId}
                      className="border-b border-border-secondary align-top last:border-b-0"
                    >
                      <td className="py-2 pr-3 whitespace-nowrap tabular-nums">
                        {h.lastUpdated
                          ? formatDate.toLocalDateTime(h.lastUpdated)
                          : 'Unknown time'}
                      </td>
                      <td className="py-2 pr-3">
                        {added.length === 0 && removed.length === 0 ? (
                          <span className="text-text-tertiary">
                            {previous ? 'No GP changes' : 'Initial version'}
                          </span>
                        ) : (
                          <div className="flex flex-col gap-0.5">
                            {added.map((ref) => (
                              <span
                                key={`add-${ref}`}
                                className="text-text-positive"
                              >
                                + {refToName(ref)}
                              </span>
                            ))}
                            {removed.map((ref) => (
                              <span
                                key={`rm-${ref}`}
                                className="text-text-negative"
                              >
                                − {refToName(ref)}
                              </span>
                            ))}
                          </div>
                        )}
                        {h.source && (
                          <div
                            className="mt-0.5 truncate font-mono text-text-tertiary"
                            title={h.source}
                          >
                            {h.source}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right text-text-tertiary tabular-nums">
                        {count}
                      </td>
                      <td className="py-2 text-right font-mono text-text-tertiary">
                        v{h.versionId.slice(0, 8)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <>
      {actionsSlot && createPortal(historyAction, actionsSlot)}
      <DataTable
        columns={columns}
        data={careTeam}
        initialSortingState={[{ id: 'name', desc: false }]}
        placeholder="No care-team members assigned to this patient."
      />
    </>
  );
};
